import { memo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
  SupportIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../../../components/AuraLayout";
import { FlowHeroMark } from "../../../components/FlowHeroMark";
import { ErrorAlertIcon as ErrorIcon } from "../../../assets/icons";
import { APP_ROUTES } from "../../../constants";
import { useStore } from "../../../store/globalState";
import { useRouteWithToken } from "../../../hooks/useRouteWithToken";
import { useSupportSheet } from "../../../hooks/useSupportSheet";
import { getEvents, safeCapture } from "../../../analytics";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";
import { Navbar } from "../../../components/Navbar";

/** Landing when cancel transfer fails (operation no longer cancellable). */
export const CancellationFailedPage = memo(() => {
  const { t } = useTranslation();
  const { operationId } = useParams<{ operationId: string }>();
  const { theme } = useStore();
  const { redirectTo } = useRouteWithToken();
  const { openSupport, supportSheet } = useSupportSheet();

  const colorConfig = theme.errorView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, iconColors, backgroundIcon } =
    colorConfig;

  useEffect(() => {
    safeCapture(getEvents().CANCELLATION_FAILED_VIEW);
  }, []);

  const handleReturn = () => {
    safeCapture(getEvents().CANCELLATION_FAILED_RETURN_CLICK);
    if (operationId) {
      redirectTo(
        APP_ROUTES.RECEIPT_SUMMARY_PAGE.replace(":operationId", operationId),
        { replace: true },
      );
      return;
    }
    redirectTo(APP_ROUTES.HOME_PAGE, { replace: true });
  };

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="need-help-header flow-landing-page">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            noBackButton
            color={title}
            onIconClick={() => openSupport()}
            icon={
              <SupportIcon size={35} colors={[backgroundIcon, backgroundIcon]} />
            }
          />
        </Layout.Header>
        <Layout.Content className="landing-section flow-hero-section flow-hero-section--with-header">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <ErrorIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("error.cancellationFailedTitle")}
                highlightColor={title}
                secudnary={t("error.cancellationFailedSubtitle")}
                secudnaryColor={title}
              />
              <BodyCopy textColor={bodyCopy}>
                {t("error.cancellationFailedMessage")}
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handleReturn}
            >
              {t("error.returnToTransactions")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
